import { extractErrorMessage } from './extractErrorMessage';
import { isPlanRefusal } from './planRefusal';

// What the AI assistant and the AI Reports page say when a turn fails
// (issue #10, 20 Oct 2026). Both used to show one apology for EVERYTHING —
// "Sorry, I could not answer right now. Please try again." — including the
// cases where trying again can never work (the hospital switched the feature
// off, the doctor's plan lacks it, the role has no access), so testers retried
// a refusal and reported the AI as broken.
//
// `source` is the rejected thunk payload (assistantSlice#assistantFailure:
// the server body + `status`, or { code: 'TIMEOUT' | 'NETWORK' }) or an axios
// error. `t` is the caller's translator; `fallback` replaces the generic
// apology (the mic has its own).

// Refusals and failures whose server sentence is the right thing to show.
const SERVER_SENTENCE = /^(FEATURE_DISABLED|ASSISTANT_[A-Z_]+)$/;

export const assistantErrorText = (source, t, lang = 'en', fallback) => {
    const generic = fallback
        || t('assistant.error', 'Sorry — I could not answer right now. Please try again.');
    const body = source?.response?.data ?? source ?? {};
    const status = source?.status ?? source?.response?.status ?? null;
    const code = typeof body?.code === 'string' ? body.code : null;
    const lng = String(lang || 'en').slice(0, 2);

    if (isPlanRefusal(body)) return extractErrorMessage(body, generic, lng);
    if (code === 'TIMEOUT' || code === 'ASSISTANT_TIMEOUT' || status === 504) {
        return t('assistant.error_timeout', 'The assistant took too long to answer. Please try again, or ask a shorter question.');
    }
    if (code === 'ASSISTANT_RATE_LIMIT' || status === 429) {
        return t('assistant.error_busy', 'Too many questions in a short time — please wait a minute and try again.');
    }
    if (code === 'FEATURE_DISABLED') {
        return t('assistant.error_feature_off', 'This AI feature is not enabled for your organisation.');
    }
    if (code && SERVER_SENTENCE.test(code)) return extractErrorMessage(body, generic, lng);
    if (status === 403) {
        return extractErrorMessage(body, t('assistant.error_forbidden', 'The assistant is not available for your account.'), lng);
    }
    if (code === 'NETWORK') {
        return t('assistant.error_network', 'No connection to the server. Check your internet connection and try again.');
    }
    return generic;
};

export default assistantErrorText;
